import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LicenseInfo } from '@mui/x-license';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Set MUI X Pro license key
LicenseInfo.setLicenseKey('6afa75ffb657fd361bd2c6a0a9392714Tz0xMjE0NDEsRT0xNzk1OTk2Nzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#115293',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
});

const PasswordProtectedApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Check for stored authentication on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('purelytics_auth');
    const storedRole = localStorage.getItem('purelytics_role');
    const sessionOnly = sessionStorage.getItem('purelytics_session_only');
    
    // If it was marked as session-only and browser was closed, don't auto-login
    // (sessionStorage is cleared on browser close, so if it exists, session is still active)
    if (storedAuth === 'true' && storedRole) {
      setIsAuthenticated(true);
      setUserRole(storedRole);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    let role = null;

    if (password === 'pureteam') {
      role = 'limited';
      setIsAuthenticated(true);
      setError(false);
      setUserRole(role);
      // Always save role to localStorage for session management
      localStorage.setItem('purelytics_auth', 'true');
      localStorage.setItem('purelytics_role', role);
      // Only persist across browser sessions if "Remember Me" is checked
      if (!rememberMe) {
        // Mark as session-only (will be cleared on browser close)
        sessionStorage.setItem('purelytics_session_only', 'true');
      } else {
        sessionStorage.removeItem('purelytics_session_only');
      }
    } else if (password === 'purebdmadmin') {
      role = 'admin';
      setIsAuthenticated(true);
      setError(false);
      setUserRole(role);
      // Always save role to localStorage for session management
      localStorage.setItem('purelytics_auth', 'true');
      localStorage.setItem('purelytics_role', role);
      // Only persist across browser sessions if "Remember Me" is checked
      if (!rememberMe) {
        // Mark as session-only (will be cleared on browser close)
        sessionStorage.setItem('purelytics_session_only', 'true');
      } else {
        sessionStorage.removeItem('purelytics_session_only');
      }
    } else {
      setError(true);
      setIsAuthenticated(false);
      setUserRole(null);
      localStorage.removeItem('purelytics_auth');
      localStorage.removeItem('purelytics_role');
      sessionStorage.removeItem('purelytics_session_only');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    localStorage.removeItem('purelytics_auth');
    localStorage.removeItem('purelytics_role');
    sessionStorage.removeItem('purelytics_session_only');
  };

  if (isAuthenticated) {
    return <App onLogout={handleLogout} userRole={userRole} />;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom align="center">
            Purelytics Dashboard
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={error}
              helperText={error ? 'Incorrect password' : ''}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label="Remember Me"
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ mt: 1 }}
            >
              Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

// Create a client with extended cache settings to minimize API calls
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 60, // 1 hour - data stays "fresh" longer
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - data stays in cache longer
      refetchOnWindowFocus: false, // Don't refetch on window focus by default
      refetchOnMount: 'always', // Always fetch on mount
      retry: 1, // Only retry once on failure
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <PasswordProtectedApp />
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
